import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { obtenerCita, actualizarEstadoCita, actualizarPago } from '../../api/citaService';
import CitaDetalle from './CitaDetalle';

const CitaDetallePage = () => {
  const { citaId } = useParams();
  const [cita, setCita] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [respuestaCompleta, setRespuestaCompleta] = useState(null);
  const [mostrarDepuracion, setMostrarDepuracion] = useState(false);

  const formatNumeroCita = (numero) => {
    if (typeof numero !== 'number') return null;
    return `C-${String(numero).padStart(4, '0')}`;
  };

  useEffect(() => {
    const cargarCitaDetalle = async () => {
      try {
        setIsLoading(true);
        
        console.log('Cargando detalles de la cita con ID:', citaId);
        
        if (!citaId) {
          setError('ID de cita no proporcionado');
          setIsLoading(false);
          return;
        }
        
        const response = await obtenerCita(citaId);
        
        // Guardar la respuesta completa para depuración
        setRespuestaCompleta(response);
        
        console.log('Respuesta del servidor (estructura):', {
          tieneData: Boolean(response?.data),
          tipoData: typeof response?.data,
          esDataObjeto: typeof response?.data === 'object',
          tieneInnerData: Boolean(response?.data?.data),
          tieneError: Boolean(response?.data?.error),
          tieneCita: Boolean(response?.data?.cita),
        });
        
        // Determinar dónde está la información de la cita en la respuesta
        let citaData;
        
        // Diferentes estructuras de respuesta posibles
        if (response?.data?.data) {
          // Estructura: { data: { data: {...citaData} } }
          citaData = response.data.data;
        } else if (response?.data?.cita) {
          // Estructura: { data: { cita: {...citaData} } }
          citaData = response.data.cita;
        } else if (typeof response?.data === 'object' && !response?.data?.error) {
          // Estructura: { data: {...citaData} }
          citaData = response.data;
        } else {
          // No se encontró una estructura reconocible
          console.error('Estructura de respuesta no reconocida:', response);
          setError('Formato de respuesta no reconocido');
          setIsLoading(false);
          return;
        }
        
        console.log('Datos de cita extraídos:', citaData);
        
        // Convertir fechas de string a objeto Date si es necesario
        if (citaData.fecha && typeof citaData.fecha === 'string') {
          citaData.fecha = new Date(citaData.fecha);
        }
        
        if (citaData.pago && citaData.pago.fechaPago && typeof citaData.pago.fechaPago === 'string') {
          citaData.pago.fechaPago = new Date(citaData.pago.fechaPago);
        }
        
        // Asegurarse de que la propiedad estado existe
        if (!citaData.estado) {
          citaData.estado = 'PENDIENTE';
          console.warn('La cita no tiene estado, usando valor por defecto:', citaData.estado);
        }
        
        // Asegurarse de que la propiedad pago existe
        if (!citaData.pago) {
          citaData.pago = {
            estado: 'PENDIENTE',
            metodo: 'EFECTIVO'
          };
          console.warn('La cita no tiene información de pago, usando valores por defecto');
        }
        
        setCita(citaData);
      } catch (err) {
        console.error('Error al cargar detalles de la cita:', err);
        setError(`Error al cargar detalles de la cita: ${err.message || 'Error desconocido'}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (citaId) {
      cargarCitaDetalle();
    } else {
      setError('No se proporcionó un ID de cita válido');
      setIsLoading(false);
    }
  }, [citaId]);

  const handleActualizarCita = async (nuevoEstado) => {
    try {
      setIsLoading(true);
      
      console.log(`Actualizando estado de cita ${citaId} a: ${nuevoEstado}`);
      
      const response = await actualizarEstadoCita(citaId, { estado: nuevoEstado });
      console.log('Respuesta de actualización de estado:', response);
      
      // Actualizar el estado en el componente
      setCita(prevCita => ({
        ...prevCita,
        estado: nuevoEstado
      }));
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error al actualizar estado de la cita:', err);
      setError(`Error al actualizar el estado de la cita: ${err.message || 'Error desconocido'}`);
      setIsLoading(false);
    }
  };

  const handleActualizarPago = async (nuevoEstadoPago, metodo) => {
    try {
      setIsLoading(true);
      
      console.log(`Actualizando estado de pago de cita ${citaId} a: ${nuevoEstadoPago}`);
      
      const response = await actualizarPago(citaId, { 
        estado: nuevoEstadoPago,
        metodo: metodo || cita.pago.metodo
      });
      
      console.log('Respuesta de actualización de pago:', response);
      
      // Actualizar el estado en el componente
      setCita(prevCita => ({
        ...prevCita,
        pago: {
          ...prevCita.pago,
          estado: nuevoEstadoPago,
          metodo: metodo || prevCita.pago.metodo,
          fechaPago: nuevoEstadoPago === 'COMPLETADO' ? new Date() : prevCita.pago.fechaPago
        }
      }));
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error al actualizar estado del pago:', err);
      setError(`Error al actualizar el estado del pago: ${err.message || 'Error desconocido'}`);
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={() => setMostrarDepuracion(!mostrarDepuracion)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 15px',
            backgroundColor: '#e3f2fd',
            border: 'none',
            borderRadius: '4px',
            color: '#0d47a1',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          <i className="fas fa-bug" style={{ marginRight: '5px' }}></i>
          {mostrarDepuracion ? 'Ocultar depuración' : 'Mostrar depuración'}
        </button>
      </div>
      
      <h2 style={{ marginBottom: '20px' }}>
        Detalle de Cita {formatNumeroCita(cita?.numero) ? `#${formatNumeroCita(cita.numero)}` : ''}
      </h2>
      
      {mostrarDepuracion && respuestaCompleta && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#e3f2fd', 
          color: '#0d47a1',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'auto',
          maxHeight: '300px'
        }}>
          <h4 style={{ marginTop: 0 }}>Información de depuración:</h4>
          <p><strong>ID de cita recibido:</strong> {citaId}</p>
          <p><strong>URL de la petición:</strong> GET /api/citas/{citaId}</p>
          <p><strong>Estructura de la respuesta:</strong></p>
          <pre style={{ background: '#f8f9fa', padding: '10px', borderRadius: '4px' }}>
            {JSON.stringify(respuestaCompleta, null, 2)}
          </pre>
        </div>
      )}
      
      {isLoading && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p>Cargando información de la cita...</p>
        </div>
      )}
      
      {error && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#ffebee', 
          color: '#c62828',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p>{error}</p>
        </div>
      )}
      
      {!isLoading && !error && cita && (
        <CitaDetalle 
          cita={cita} 
          onActualizarEstado={handleActualizarCita} 
          onActualizarPago={handleActualizarPago} 
        />
      )}
      
      {!isLoading && !error && !cita && (
        <div style={{ 
          padding: '30px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p>No se encontró información para la cita con ID: {citaId}</p>
        </div>
      )}
    </div>
  );
};

export default CitaDetallePage;